import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Building2,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  BarChart3,
  Plus,
  Users,
  GraduationCap,
  Clock,
  BookOpen,
  DoorOpen,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { RoomOccupancyButton } from '../components/room-occupancy/RoomOccupancyButton';
import { Permission } from '@/types/global.types';
import { getAvailableCampuses } from '@/services/groupsService';
import { fetchAcademicYears, AcademicYear } from '@/services/academicPlanningService';
import examScheduleService, {
  ExamCalendarData,
  ExamCalendarEntry,
  ExamPeriods,
  RattrapageGroupInfo,
} from '../services/examScheduleService';
import ExamEntryModal from '../components/exam-scheduling/ExamEntryModal';
import { RattrapageSchedulingProgressPanel } from '../components/exam-scheduling/RattrapageSchedulingProgressPanel';

interface Campus {
  id: string;
  name: string;
  sigle: string | null;
  isMainCampus: boolean;
}

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const RattrapageSchedulingPage: React.FC = () => {
  const { tokens } = useAuthStore();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.RATTRAPAGE_SCHEDULE_MANAGE);

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Data State
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<AcademicYear | null>(null);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState<string | null>(null);

  // Semester selection
  const [selectedSemester, setSelectedSemester] = useState<1 | 2>(1);

  // Rattrapage groups - only groups with students in rattrapage
  const [rattrapageGroups, setRattrapageGroups] = useState<RattrapageGroupInfo[]>([]);
  const [loadingRattrapageGroups, setLoadingRattrapageGroups] = useState(false);
  const [selectedGroupInfo, setSelectedGroupInfo] = useState<RattrapageGroupInfo | null>(null);

  // Selected matière for drag-and-drop scheduling
  const [selectedMatiereId, setSelectedMatiereId] = useState<string | null>(null);

  // Calendar state - weekly view
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [calendarData, setCalendarData] = useState<ExamCalendarData | null>(null);
  const [examPeriods, setExamPeriods] = useState<ExamPeriods | null>(null);

  // Modals
  const [showRattrapageModal, setShowRattrapageModal] = useState(false);
  const [showProgressPanel, setShowProgressPanel] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [examToEdit, setExamToEdit] = useState<ExamCalendarEntry | null>(null);

  // Time slot selection for drag
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDate, setDragStartDate] = useState<string | null>(null);
  const [dragStartHour, setDragStartHour] = useState<number | null>(null);
  const [dragCurrentHour, setDragCurrentHour] = useState<number | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null);
  const [selectedEndTime, setSelectedEndTime] = useState<string | null>(null);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    if (!tokens?.accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const [yearsData, campusData] = await Promise.all([
        fetchAcademicYears(tokens.accessToken),
        getAvailableCampuses(),
      ]);

      setAcademicYears(yearsData);
      setCampuses(campusData.campuses);
      setHasMultipleCampuses(campusData.hasMultipleCampuses);

      const currentYear = yearsData.find((y: AcademicYear) => y.isCurrent);
      if (currentYear) {
        setSelectedAcademicYear(currentYear);
      }

      if (campusData.hasMultipleCampuses && campusData.campuses.length > 0) {
        const mainCampus = campusData.campuses.find((c: Campus) => c.isMainCampus);
        setSelectedCampusId(mainCampus?.id || campusData.campuses[0].id);
      } else if (campusData.campuses.length === 1) {
        setSelectedCampusId(campusData.campuses[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  }, [tokens?.accessToken]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load rattrapage groups when academic year or campus changes
  useEffect(() => {
    const loadRattrapageGroups = async () => {
      if (!selectedAcademicYear) {
        setRattrapageGroups([]);
        return;
      }

      setLoadingRattrapageGroups(true);
      try {
        const groups = await examScheduleService.getGroupsWithRattrapageStudents(
          selectedAcademicYear.id,
          selectedCampusId || undefined
        );
        setRattrapageGroups(groups);
      } catch (err) {
        console.error('Failed to load rattrapage groups:', err);
        setRattrapageGroups([]);
      } finally {
        setLoadingRattrapageGroups(false);
      }
    };

    loadRattrapageGroups();
  }, [selectedAcademicYear, selectedCampusId]);

  // Load exam periods
  useEffect(() => {
    const loadExamPeriods = async () => {
      if (!selectedAcademicYear) return;
      try {
        // Campus-specific periods when the exam config is in per-campus mode
        const periods = await examScheduleService.getExamPeriods(selectedAcademicYear.id, selectedCampusId || undefined);
        setExamPeriods(periods);
      } catch (err) {
        console.error('Error loading exam periods:', err);
      }
    };
    loadExamPeriods();
  }, [selectedAcademicYear, selectedCampusId]);

  // Load calendar data (only rattrapages)
  useEffect(() => {
    const loadCalendar = async () => {
      if (!selectedAcademicYear || !selectedCampusId) {
        setCalendarData(null);
        return;
      }
      try {
        const data = await examScheduleService.getCalendar(
          selectedAcademicYear.id,
          selectedCampusId,
          currentMonth,
          currentYear,
          selectedGroupInfo?.filiere.id || undefined,
          selectedGroupInfo?.groupe.id || undefined
        );

        // Filter to show only rattrapages
        const filteredExams: Record<string, ExamCalendarEntry[]> = {};
        Object.entries(data.exams).forEach(([date, exams]) => {
          const rattrapages = exams.filter(e => e.isRattrapage);
          if (rattrapages.length > 0) {
            filteredExams[date] = rattrapages;
          }
        });

        setCalendarData({ ...data, exams: filteredExams });
      } catch (err) {
        console.error('Error loading calendar:', err);
      }
    };
    loadCalendar();
  }, [selectedAcademicYear, selectedCampusId, selectedGroupInfo, currentMonth, currentYear]);

  // Helper to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (year: number, month: number, day: number): string => {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Weekly view navigation
  const handlePrevWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
    setCurrentMonth(newStart.getMonth() + 1);
    setCurrentYear(newStart.getFullYear());
  };

  const handleNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
    setCurrentMonth(newStart.getMonth() + 1);
    setCurrentYear(newStart.getFullYear());
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth() + 1);
    setCurrentYear(today.getFullYear());
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(new Date().setDate(diff)));
  };

  // Calculate week days for weekly view
  const weekDays = useMemo(() => {
    const days: { date: string; dayNum: number; dayName: string; isToday: boolean; exams: ExamCalendarEntry[] }[] = [];
    const weekDayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    const today = new Date().toISOString().split('T')[0];

    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      const dateStr = formatDateString(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const exams = calendarData?.exams[dateStr] || [];

      days.push({
        date: dateStr,
        dayNum: date.getDate(),
        dayName: weekDayNames[i],
        isToday: dateStr === today,
        exams,
      });
    }
    return days;
  }, [currentWeekStart, calendarData]);

  // Get week range label
  const weekRangeLabel = useMemo(() => {
    const endOfWeek = new Date(currentWeekStart);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    const startMonth = MONTHS[currentWeekStart.getMonth()];
    const endMonth = MONTHS[endOfWeek.getMonth()];

    if (currentWeekStart.getMonth() === endOfWeek.getMonth()) {
      return `${currentWeekStart.getDate()} - ${endOfWeek.getDate()} ${startMonth} ${currentWeekStart.getFullYear()}`;
    }
    return `${currentWeekStart.getDate()} ${startMonth} - ${endOfWeek.getDate()} ${endMonth} ${endOfWeek.getFullYear()}`;
  }, [currentWeekStart]);

  // Time slot drag handlers
  const handleTimeSlotMouseDown = (date: string, hour: number) => {
    if (!canManage) return;
    setIsDragging(true);
    setDragStartDate(date);
    setDragStartHour(hour);
    setDragCurrentHour(hour);
    setSelectedDate(date);
  };

  const handleTimeSlotMouseEnter = (hour: number) => {
    if (isDragging && dragStartHour !== null) {
      setDragCurrentHour(hour);
    }
  };

  const handleTimeSlotMouseUp = () => {
    if (!isDragging || dragStartHour === null || dragCurrentHour === null || !dragStartDate) {
      setIsDragging(false);
      return;
    }

    const startHour = Math.min(dragStartHour, dragCurrentHour);
    const endHour = Math.max(dragStartHour, dragCurrentHour) + 1;

    const startTimeStr = `${startHour.toString().padStart(2, '0')}:00`;
    const endTimeStr = `${endHour.toString().padStart(2, '0')}:00`;

    setSelectedStartTime(startTimeStr);
    setSelectedEndTime(endTimeStr);
    setSelectedDate(dragStartDate);
    setEditMode(false);
    setExamToEdit(null);
    setShowRattrapageModal(true);

    setIsDragging(false);
    setDragStartDate(null);
    setDragStartHour(null);
    setDragCurrentHour(null);
  };

  const handleGridMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setDragStartDate(null);
      setDragStartHour(null);
      setDragCurrentHour(null);
    }
  };

  const isTimeSlotInDragRange = (date: string, hour: number) => {
    if (!isDragging || dragStartDate !== date || dragStartHour === null || dragCurrentHour === null) {
      return false;
    }
    const minHour = Math.min(dragStartHour, dragCurrentHour);
    const maxHour = Math.max(dragStartHour, dragCurrentHour);
    return hour >= minHour && hour <= maxHour;
  };

  const handleAddRattrapage = (matiereId?: string) => {
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setEditMode(false);
    setExamToEdit(null);
    if (matiereId) {
      setSelectedMatiereId(matiereId);
    }
    setShowRattrapageModal(true);
  };

  const handleEditExam = (exam: ExamCalendarEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canManage) return;

    setEditMode(true);
    setExamToEdit(exam);
    setSelectedDate(exam.examDate);
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setShowRattrapageModal(true);
  };

  const handleRattrapageCreated = () => {
    setShowRattrapageModal(false);
    setEditMode(false);
    setExamToEdit(null);
    setSuccess(editMode ? 'Rattrapage modifié avec succès' : 'Rattrapage créé avec succès');
    // Reload calendar
    if (selectedAcademicYear && selectedCampusId) {
      examScheduleService
        .getCalendar(
          selectedAcademicYear.id,
          selectedCampusId,
          currentMonth,
          currentYear,
          selectedGroupInfo?.filiere.id || undefined,
          selectedGroupInfo?.groupe.id || undefined
        )
        .then(data => {
          const filteredExams: Record<string, ExamCalendarEntry[]> = {};
          Object.entries(data.exams).forEach(([date, exams]) => {
            const rattrapages = exams.filter(e => e.isRattrapage);
            if (rattrapages.length > 0) {
              filteredExams[date] = rattrapages;
            }
          });
          setCalendarData({ ...data, exams: filteredExams });
        })
        .catch(console.error);
    }
  };

  const handleExamDeleted = () => {
    setSuccess('Rattrapage supprimé avec succès');
  };

  // Calculate scheduled rattrapages per matière for progress tracking
  const scheduledRattrapagesMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!calendarData) return map;

    Object.values(calendarData.exams).forEach(dayExams => {
      dayExams.forEach(exam => {
        const current = map.get(exam.moduleElement.id) || 0;
        map.set(exam.moduleElement.id, current + 1);
      });
    });

    return map;
  }, [calendarData]);

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  // Filter rattrapage groups by search
  const filteredRattrapageGroups = rattrapageGroups.filter((g) => {
    const query = searchQuery.toLowerCase();
    return (
      g.groupe.name.toLowerCase().includes(query) ||
      g.filiere.filiere.toLowerCase().includes(query) ||
      g.filiere.codeFiliere.toLowerCase().includes(query)
    );
  });

  // Get matières for the selected group, filtered by semester
  const matieres = useMemo(() => {
    if (!selectedGroupInfo) return [];
    const niveau = selectedGroupInfo.groupe.niveau;
    const expectedSemesterNum = (niveau - 1) * 2 + selectedSemester;

    return selectedGroupInfo.moduleElements
      .filter(me => !me.semesterNumber || me.semesterNumber === expectedSemesterNum)
      .map(me => ({
        id: me.id,
        name: me.name,
        weight: 0,
        studentCount: me.studentCount,
        semesterNumber: me.semesterNumber,
      }));
  }, [selectedGroupInfo, selectedSemester]);

  // List of allowed module element IDs for the modal (only rattrapage matières)
  const allowedModuleElementIds = useMemo(() => {
    if (!selectedGroupInfo) return undefined;
    return selectedGroupInfo.moduleElements.map(me => me.id);
  }, [selectedGroupInfo]);

  const getNiveauLabel = (niveau: number): string => {
    const suffixes: Record<number, string> = {
      1: '1ère Année',
      2: '2ème Année',
      3: '3ème Année',
      4: '4ème Année',
      5: '5ème Année',
    };
    return suffixes[niveau] || `${niveau}ème Année`;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-amber-50 rounded-lg">
              <RefreshCw className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900">
                Planification des Rattrapages
              </h1>
              <p className="text-xs text-gray-500">
                Planifiez les sessions de rattrapage pour les groupes concernés
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <RoomOccupancyButton
              campuses={campuses}
              campusId={selectedCampusId}
              date={currentWeekStart}
              academicYearId={selectedAcademicYear?.id}
            />

            {/* Campus Selector */}
            {hasMultipleCampuses && campuses.length > 1 && (
              <div className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-gray-500" />
                <select
                  value={selectedCampusId || ''}
                  onChange={(e) => {
                    setSelectedCampusId(e.target.value);
                    setSelectedGroupInfo(null);
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50"
                >
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.sigle || campus.name} {campus.isMainCampus ? '(Principal)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Academic Year Selector */}
            <select
              value={selectedAcademicYear?.id || ''}
              onChange={(e) => {
                const year = academicYears.find(y => y.id === e.target.value);
                setSelectedAcademicYear(year || null);
                setSelectedGroupInfo(null);
              }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              {academicYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name} {year.isCurrent ? '(Courante)' : ''}
                </option>
              ))}
            </select>

            {/* S1/S2 Toggle */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedSemester(1)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  selectedSemester === 1
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                S1
              </button>
              <button
                onClick={() => setSelectedSemester(2)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  selectedSemester === 2
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                S2
              </button>
            </div>

            {/* Add Rattrapage Button */}
            {canManage && selectedGroupInfo && (
              <button
                onClick={() => handleAddRattrapage()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Ajouter un rattrapage
              </button>
            )}

            {/* Progress Panel Toggle */}
            <button
              onClick={() => setShowProgressPanel(!showProgressPanel)}
              className={`p-1.5 rounded-lg transition-colors ${
                showProgressPanel
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
              }`}
              title={showProgressPanel ? 'Masquer la progression' : 'Afficher la progression'}
            >
              <BarChart3 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-xs flex-shrink-0">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {success && (
        <div className="mx-4 mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-xs flex-shrink-0">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <div className="flex-1 p-3 overflow-hidden">
            <div className="grid grid-cols-12 gap-3 h-full">
              {/* Left Sidebar - Rattrapage Groups and Matières */}
              <aside className="col-span-3 flex flex-col gap-2 overflow-hidden">
                {/* Groups with Rattrapage */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col flex-shrink-0" style={{ maxHeight: selectedGroupInfo ? '45%' : '70%' }}>
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                        <RefreshCw className="h-3.5 w-3.5" />
                        Groupes en rattrapage
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                        {rattrapageGroups.length} groupe{rattrapageGroups.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Search */}
                  <div className="p-2 border-b border-gray-100 flex-shrink-0">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Rechercher un groupe..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>

                  {/* Groups List */}
                  <div className="flex-1 overflow-y-auto min-h-0">
                    {loadingRattrapageGroups ? (
                      <div className="p-3 flex justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      </div>
                    ) : filteredRattrapageGroups.length === 0 ? (
                      <div className="p-4 text-center">
                        <RefreshCw className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">
                          {rattrapageGroups.length === 0
                            ? 'Aucun groupe avec des étudiants en rattrapage'
                            : 'Aucun groupe trouvé pour cette recherche'
                          }
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {filteredRattrapageGroups.map((groupInfo) => (
                          <button
                            key={groupInfo.groupe.id}
                            onClick={() => setSelectedGroupInfo(
                              selectedGroupInfo?.groupe.id === groupInfo.groupe.id ? null : groupInfo
                            )}
                            className={`w-full px-3 py-2.5 text-left transition-colors ${
                              selectedGroupInfo?.groupe.id === groupInfo.groupe.id
                                ? 'bg-amber-50 border-l-3 border-amber-500'
                                : 'hover:bg-gray-50 border-l-3 border-transparent'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 leading-snug break-words" title={groupInfo.groupe.name}>
                                  {groupInfo.groupe.name}
                                </p>
                                <p className="text-[10px] text-gray-500 leading-snug break-words">
                                  {groupInfo.filiere.codeFiliere} - {getNiveauLabel(groupInfo.groupe.niveau)}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                                  {groupInfo.rattrapageStudentCount} rattr.
                                </span>
                                <span className="text-[10px] text-gray-400">
                                  {groupInfo.moduleElements.length} matière{groupInfo.moduleElements.length > 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Matières Panel - Module elements with rattrapage for selected group */}
                {selectedGroupInfo && (
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                        <BookOpen className="h-3.5 w-3.5" />
                        Matières en rattrapage - S{selectedSemester}
                      </div>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {selectedGroupInfo.groupe.name} ({selectedGroupInfo.filiere.codeFiliere})
                      </p>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0">
                      {matieres.length === 0 ? (
                        <div className="p-3 text-center text-gray-500 text-xs">
                          Aucune matière en rattrapage
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {matieres.map((matiere) => {
                            const hasScheduled = scheduledRattrapagesMap.has(matiere.id);
                            const scheduledCount = scheduledRattrapagesMap.get(matiere.id) || 0;
                            const isSelected = selectedMatiereId === matiere.id;

                            return (
                              <button
                                key={matiere.id}
                                onClick={() => {
                                  if (selectedMatiereId === matiere.id) {
                                    setSelectedMatiereId(null);
                                  } else {
                                    setSelectedMatiereId(matiere.id);
                                  }
                                }}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('matiereId', matiere.id);
                                  e.dataTransfer.setData('matiereName', matiere.name);
                                  setSelectedMatiereId(matiere.id);
                                }}
                                className={`w-full px-3 py-2 flex items-center gap-2 transition-all text-left ${
                                  isSelected
                                    ? 'bg-amber-50 border-l-3 border-amber-500'
                                    : 'hover:bg-gray-50 border-l-3 border-transparent'
                                }`}
                              >
                                <div
                                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    hasScheduled ? 'bg-amber-500' : 'bg-gray-300'
                                  }`}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-900 leading-snug line-clamp-2" title={matiere.name}>
                                    {matiere.name}
                                  </p>
                                  <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                    <span className="flex items-center gap-0.5">
                                      <Users className="h-2.5 w-2.5" />
                                      {matiere.studentCount} étud.
                                    </span>
                                    {matiere.semesterNumber && (
                                      <span>S{matiere.semesterNumber}</span>
                                    )}
                                    {hasScheduled && (
                                      <span className="text-amber-600">
                                        {scheduledCount} planifié{scheduledCount > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Tips */}
                {!selectedGroupInfo && !loadingRattrapageGroups && rattrapageGroups.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex-shrink-0">
                    <h4 className="text-xs font-medium text-amber-800 mb-1.5">
                      Comment utiliser
                    </h4>
                    <ul className="text-[10px] text-amber-700 space-y-0.5">
                      <li>1. Sélectionnez un groupe en rattrapage</li>
                      <li>2. Consultez les matières concernées</li>
                      <li>3. Glissez sur le calendrier pour planifier</li>
                      <li>4. Ou cliquez sur &quot;Ajouter un rattrapage&quot;</li>
                    </ul>
                  </div>
                )}
              </aside>

              {/* Main Content - Weekly Calendar */}
              <main className="col-span-9 flex flex-col overflow-hidden">
                {/* Week Navigation */}
                <div className="flex items-center justify-between mb-2 bg-white rounded-lg border border-gray-200 p-2 flex-shrink-0">
                  <button
                    onClick={handlePrevWeek}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-900">
                      {weekRangeLabel}
                    </span>
                    <button
                      onClick={handleToday}
                      className="px-2 py-1 text-xs font-medium text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                    >
                      Aujourd'hui
                    </button>
                  </div>

                  <button
                    onClick={handleNextWeek}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                {/* Calendar Grid */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
                  {!selectedGroupInfo ? (
                    <div className="p-8 text-center h-full flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <Users className="h-6 w-6 text-gray-400" />
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        Sélectionnez un groupe
                      </h3>
                      <p className="text-xs text-gray-500">
                        Choisissez un groupe en rattrapage pour voir et planifier ses sessions
                      </p>
                    </div>
                  ) : (
                    /* WEEKLY VIEW WITH TIME GRID */
                    <div className="flex-1 flex flex-col overflow-hidden">
                      {/* Week header with days */}
                      <div className="flex border-b border-gray-200 flex-shrink-0">
                        <div className="w-14 flex-shrink-0 bg-gray-50 border-r border-gray-200" />
                        <div className="flex-1 grid grid-cols-7">
                          {weekDays.map((day) => {
                            const isWeekend = day.dayName === 'Sam' || day.dayName === 'Dim';
                            return (
                              <div
                                key={day.date}
                                className={`text-center py-2 border-r border-gray-100 last:border-r-0 ${
                                  isWeekend ? 'bg-gray-100' : 'bg-gray-50'
                                }`}
                              >
                                <p className={`text-[10px] font-medium ${isWeekend ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {day.dayName}
                                </p>
                                <p className={`text-sm font-semibold ${
                                  day.isToday
                                    ? 'bg-amber-600 text-white rounded-full w-6 h-6 flex items-center justify-center mx-auto'
                                    : isWeekend ? 'text-gray-400' : 'text-gray-900'
                                }`}>
                                  {day.dayNum}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Time grid with slots */}
                      <div
                        className="flex-1 flex overflow-y-auto select-none"
                        onMouseLeave={handleGridMouseLeave}
                        onMouseUp={handleTimeSlotMouseUp}
                      >
                        {/* Time labels column */}
                        <div className="w-14 flex-shrink-0 bg-gray-50 border-r border-gray-200">
                          {Array.from({ length: 13 }, (_, i) => i + 8).map((hour) => (
                            <div
                              key={hour}
                              className="h-12 border-b border-gray-100 flex items-start justify-end pr-2 pt-0.5"
                            >
                              <span className="text-[10px] text-gray-500 font-medium">
                                {hour.toString().padStart(2, '0')}:00
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Day columns with time slots */}
                        <div className="flex-1 grid grid-cols-7 relative">
                          {weekDays.map((day) => {
                            const isSelected = selectedDate === day.date;
                            const isWeekend = day.dayName === 'Sam' || day.dayName === 'Dim';

                            const getExamPosition = (startTime: string, endTime: string) => {
                              const [startHour, startMin] = startTime.split(':').map(Number);
                              const [endHour, endMin] = endTime.split(':').map(Number);
                              const startMinutes = (startHour - 8) * 60 + startMin;
                              const endMinutes = (endHour - 8) * 60 + endMin;
                              const top = (startMinutes / 60) * 48;
                              const height = ((endMinutes - startMinutes) / 60) * 48;
                              return { top: Math.max(0, top), height: Math.max(24, height) };
                            };

                            return (
                              <div
                                key={day.date}
                                className={`relative border-r border-gray-100 last:border-r-0 ${
                                  isWeekend ? 'bg-gray-50' : ''
                                } ${isSelected && !isDragging ? 'bg-amber-50' : ''}`}
                              >
                                {/* Hour grid lines */}
                                {Array.from({ length: 13 }, (_, i) => i + 8).map((hour) => {
                                  const isInDragRange = isTimeSlotInDragRange(day.date, hour);
                                  return (
                                    <div
                                      key={hour}
                                      onMouseDown={() => !isWeekend && handleTimeSlotMouseDown(day.date, hour)}
                                      onMouseEnter={() => handleTimeSlotMouseEnter(hour)}
                                      onDragOver={(e) => {
                                        if (!isWeekend) {
                                          e.preventDefault();
                                          e.dataTransfer.dropEffect = 'copy';
                                        }
                                      }}
                                      onDrop={(e) => {
                                        e.preventDefault();
                                        const matiereId = e.dataTransfer.getData('matiereId');
                                        if (matiereId && !isWeekend) {
                                          const startTimeStr = `${hour.toString().padStart(2, '0')}:00`;
                                          const endTimeStr = `${(hour + 2).toString().padStart(2, '0')}:00`;
                                          setSelectedDate(day.date);
                                          setSelectedStartTime(startTimeStr);
                                          setSelectedEndTime(endTimeStr);
                                          setSelectedMatiereId(matiereId);
                                          setEditMode(false);
                                          setExamToEdit(null);
                                          setShowRattrapageModal(true);
                                        }
                                      }}
                                      className={`h-12 border-b border-gray-100 transition-colors ${
                                        !isWeekend && canManage ? 'cursor-pointer' : ''
                                      } ${isInDragRange ? 'bg-amber-200' : !isWeekend && canManage ? 'hover:bg-amber-100/50' : ''}`}
                                    />
                                  );
                                })}

                                {/* Exams positioned by time */}
                                {day.exams.map((exam, i) => {
                                  const pos = getExamPosition(exam.startTime, exam.endTime);
                                  return (
                                    <div
                                      key={i}
                                      onClick={(e) => handleEditExam(exam, e)}
                                      className={`
                                        absolute left-0.5 right-0.5 rounded-md border shadow-sm overflow-hidden
                                        transition-all hover:shadow-md hover:z-10
                                        ${canManage ? 'cursor-pointer' : 'cursor-default'}
                                        bg-amber-100 border-amber-300 hover:bg-amber-200
                                      `}
                                      style={{ top: `${pos.top}px`, height: `${pos.height}px`, minHeight: '24px' }}
                                      title={`${exam.moduleElement.name}\n${exam.startTime} - ${exam.endTime}\n${exam.groupe.name}\nSalle: ${exam.room?.name || 'Non assignée'}\nSurveillant: ${exam.isExternalProctor ? 'Externe' : exam.professor ? `${exam.professor.firstName} ${exam.professor.lastName}` : 'Non assigné'}${canManage ? '\n\nCliquez pour modifier' : ''}`}
                                    >
                                      <div className="p-1 h-full flex flex-col gap-0.5">
                                        <div className="flex items-center gap-0.5 flex-shrink-0">
                                          <Clock className="h-2.5 w-2.5 text-gray-500 flex-shrink-0" />
                                          <span className="text-[9px] font-medium text-gray-600 truncate">
                                            {exam.startTime}-{exam.endTime}
                                          </span>
                                        </div>
                                        <p className="text-[10px] font-semibold text-amber-800 truncate leading-tight">
                                          {exam.moduleElement.name}
                                        </p>
                                        {pos.height > 40 && (
                                          <>
                                            <p className="text-[9px] text-gray-600 truncate leading-tight">
                                              {exam.groupe.name}
                                            </p>
                                            {pos.height > 50 && exam.room && (
                                              <div className="flex items-center gap-0.5">
                                                <DoorOpen className="h-2 w-2 text-blue-600 flex-shrink-0" />
                                                <span className="text-[9px] text-blue-600 font-medium truncate">
                                                  {exam.room.name}
                                                </span>
                                              </div>
                                            )}
                                            {pos.height > 65 && (
                                              <div className="flex items-center gap-0.5">
                                                <GraduationCap className="h-2 w-2 text-gray-500 flex-shrink-0" />
                                                <span className="text-[9px] text-gray-600 truncate">
                                                  {exam.isExternalProctor
                                                    ? 'Surveillant externe'
                                                    : exam.professor
                                                    ? `${exam.professor.firstName} ${exam.professor.lastName}`
                                                    : 'Non assigné'}
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </main>
            </div>
          </div>
        )}

        {/* Progress Panel */}
        {showProgressPanel && selectedGroupInfo && (
          <RattrapageSchedulingProgressPanel
            matieres={matieres.map(m => ({
              id: m.id,
              name: m.name,
              weight: m.weight,
              volumeHoraire: 0,
              moduleId: '',
              isActive: true,
              sortOrder: 0,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }))}
            scheduledRattrapagesMap={scheduledRattrapagesMap}
            groupName={selectedGroupInfo.groupe.name}
            examPeriods={examPeriods}
          />
        )}
      </div>

      {/* Rattrapage Entry Modal */}
      {showRattrapageModal && selectedAcademicYear && selectedCampusId && (
        <ExamEntryModal
          academicYearId={selectedAcademicYear.id}
          campusId={selectedCampusId}
          campusName={campuses.find((c) => c.id === selectedCampusId)?.name}
          filiereId={selectedGroupInfo?.filiere.id}
          groupeId={selectedGroupInfo?.groupe.id}
          niveau={selectedGroupInfo?.groupe.niveau}
          moduleElementId={selectedMatiereId || undefined}
          selectedDate={selectedDate}
          selectedStartTime={selectedStartTime}
          selectedEndTime={selectedEndTime}
          defaultSemester={selectedSemester}
          isRattrapage={true}
          allowedModuleElementIds={allowedModuleElementIds}
          editMode={editMode}
          examToEdit={examToEdit}
          onClose={() => {
            setShowRattrapageModal(false);
            setSelectedStartTime(null);
            setSelectedEndTime(null);
            setEditMode(false);
            setExamToEdit(null);
            setSelectedMatiereId(null);
          }}
          onSuccess={handleRattrapageCreated}
          onDelete={handleExamDeleted}
        />
      )}
    </div>
  );
};

export default RattrapageSchedulingPage;
